import { Box, CircularProgress, IconButton, Tooltip } from "@mui/material"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import Iconify from "minimals-template/components/Iconify"
import { HighlightBox } from "lib/@components/highlightBox"
import { linkedButtonSx, titleLinkButtonSx } from "./component-link-button-styles"
import { preventPropagation } from "lib/prevent"

const FIELD = "title"
const ICON_SX = { width: 14, height: 14 }

/**
 * Slot plug for TaskTitle.
 *
 * - No componentRef: renders the default title editor
 * - ComponentRef set, field not linked: puzzle icon toggles field on (if component has a heading)
 * - Field linked: shows inherited heading + link icon to unlink
 */
export function ComponentRefTitle({ originalchildren }) {
    const { componentRef, linkedFields, onEnableField, onDisableField, editMode, componentTask } = useBoundContext()
    const isLinked = !!componentRef && linkedFields?.[FIELD]

    if (!isLinked) {
        const canLink = !!componentRef && componentHasHeading(componentTask)

        return (
            <Box display="inline-flex" alignItems="center" gap={0.5} sx={{ flex: 1, width: 1 }}>
                {originalchildren}
                {editMode && canLink && (
                    <Tooltip title="Link title from component">
                        <IconButton
                            size="small"
                            onClick={preventPropagation(handleEnable)}
                            className="component-link-btn"
                            sx={titleLinkButtonSx}
                            aria-label="Link title"
                        >
                            <Iconify icon="mdi:puzzle-plus-outline" sx={ICON_SX} />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
        )
    }

    if (componentTask === IS_LOADING) {
        return (
            <Box display="inline-flex" alignItems="center" sx={{ width: 24, height: 24 }}>
                <CircularProgress size={16} />
            </Box>
        )
    }

    if (!componentTask) return originalchildren ?? null

    return (
        <HighlightBox path="title" display="inline-flex" alignItems="center" width={1} flex={1}>
            <Tooltip title={`Inherited from: ${componentTask.heading ?? componentRef}`}>
                <Box display="inline-flex" alignItems="center" gap={0.5} sx={{ opacity: editMode ? 0.6 : 1, flex: 1 }}>
                    <Box component="span" sx={{ flex: 1, wordBreak: "break-word", minWidth: 100, width: 1 }}>
                        {componentTask.heading ?? ""}
                    </Box>
                    {editMode && (
                        <IconButton
                            size="small"
                            onClick={preventPropagation(handleDisable)}
                            sx={linkedButtonSx}
                            aria-label="Unlink title"
                        >
                            <Iconify icon="mdi:link-variant" sx={ICON_SX} />
                        </IconButton>
                    )}
                </Box>
            </Tooltip>
        </HighlightBox>
    )

    function handleEnable() {
        onEnableField?.(FIELD)
    }

    function handleDisable() {
        onDisableField?.(FIELD)
    }
}

function componentHasHeading(task) {
    if (!task || task === IS_LOADING) return false
    return !!task.heading
}
